import React, { useState, useCallback, useEffect } from 'react';
import useAssistant from 'hooks/useAssistant';
import Icon from '../NewIcon';
import DefaultButton from '../NewDefaultButton';
import DefaultTextField from '../DefaultTextField';
import { EventParameter } from '../../../generated/api/models/EventParameter';
import { v4 as uuidV4 } from 'uuid';
import './style.scss';
import { runSavedPromptTyped } from '../../../thunks/runSavedPromptTyped';

interface AITemplateAssistantProps {
  currentTemplate: string;
  parameters: EventParameter[];
  onApply: (template: string) => void;
  outputFormat?: 'plaintext' | 'markdown';
}

const AITemplateAssistant: React.FC<AITemplateAssistantProps> = ({
  currentTemplate,
  parameters,
  onApply,
  outputFormat = 'plaintext',
}) => {
  const [assistantId] = useState(() => `template-ai-${uuidV4()}`);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [userInstruction, setUserInstruction] = useState('');
  const [generatedTemplate, setGeneratedTemplate] = useState('');

  const { runSavedPromptTyped, streamingText, loadingReply, stopRequest } = useAssistant(
    assistantId,
    false,
    false,
  );

  // Handle AI template generation
  const handleGenerateTemplate = useCallback(async () => {
    if (!userInstruction.trim()) return;

    // Format parameters for the prompt
    const parametersInfo = parameters
      .map(
        (param) =>
          `- {{${param.name}}}: ${param.description} (${param.type}) - Example: ${
            param.example || 'N/A'
          }`,
      )
      .join('\n');

    try {
      const result = await runSavedPromptTyped(
        'Template Generator',
        {
          current_template: currentTemplate || '(empty - create from scratch)',
          available_parameters: parametersInfo || '(none available)',
          user_instruction: userInstruction,
        },
        {},
      );

      if (result) {
        setGeneratedTemplate(result);
      }
    } catch (error) {
      console.error('Failed to generate template:', error);
    }
  }, [userInstruction, currentTemplate, parameters, runSavedPromptTyped]);

  // Apply generated template
  const handleApplyTemplate = useCallback(() => {
    const templateToApply = streamingText || generatedTemplate;
    if (templateToApply) {
      onApply(templateToApply);
      setGeneratedTemplate('');
      setUserInstruction('');
      setShowAIAssistant(false);
    }
  }, [streamingText, generatedTemplate, onApply]);

  // Update generated template when streaming completes
  useEffect(() => {
    if (!loadingReply && streamingText) {
      setGeneratedTemplate(streamingText);
    }
  }, [loadingReply, streamingText]);

  const currentGenerated = streamingText || generatedTemplate;
  const hasGeneratedContent = !!currentGenerated;

  if (parameters.length === 0) {
    return null;
  }

  return (
    <div className="ai-template-assistant">
      <button
        className="ai-toggle-button"
        onClick={() => setShowAIAssistant(!showAIAssistant)}
        title="AI Template Assistant"
      >
        <Icon src="sparkles" height="14px" />
        <span>AI Assistant</span>
        <Icon src={showAIAssistant ? 'chevron-up' : 'chevron-down'} height="10px" />
      </button>

      {showAIAssistant && (
        <div className="ai-assistant-content">
          <div className="ai-input-section">
            <div className="input-row">
              <div className="text-field-wrapper">
                <DefaultTextField
                  label=""
                  value={userInstruction}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUserInstruction(e.target.value)
                  }
                  placeholder="Describe what you want in the template (e.g., 'Add a greeting with patient name and appointment details')"
                  disabled={loadingReply}
                  style={{ width: '100%' }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerateTemplate();
                    }
                  }}
                />
              </div>
              <div className="generate-button">
                {loadingReply ? (
                  <DefaultButton name="Stop" icon="circle-stop" onClick={stopRequest} color="red" />
                ) : (
                  <DefaultButton
                    name="Generate"
                    icon="sparkles"
                    onClick={handleGenerateTemplate}
                    color="blue"
                    disabled={!userInstruction.trim()}
                  />
                )}
              </div>
            </div>

            {hasGeneratedContent && !loadingReply && (
              <div className="ai-buttons">
                <DefaultButton
                  name="Apply Template"
                  icon="check"
                  style={{ width: '200px' }}
                  onClick={handleApplyTemplate}
                  color="green"
                />
              </div>
            )}
          </div>

          {/* Streaming/Generated Preview */}
          {(loadingReply || hasGeneratedContent) && (
            <div className="ai-preview">
              <div className="ai-preview-header">
                <Icon src="eye" height="12px" />
                <span>Generated Template {loadingReply && '(streaming...)'}</span>
              </div>
              <div className="ai-preview-content">
                <pre>{currentGenerated || 'Generating...'}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AITemplateAssistant;
